import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useResetPasswordStore } from "../store";
import { newPasswordReq } from "@/api/services/auth";
import type { ResetPasswordFormData } from "../store/types";
import { paths } from "@/navigation/paths";

type FieldErrors = Partial<Record<keyof ResetPasswordFormData, string>>;

export const useResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [fieldsErrors, setFieldsErrors] = useState<FieldErrors>({});

  const {
    password,
    confirmPassword,
    setPassword,
    setConfirmPassword,
    setLoading,
    setError,
    isLoading,
    errorMessage,
  } = useResetPasswordStore();

  const phone = location.state?.phone || "";
  const code = location.state?.code || "";

  // Clear error when component mounts
  useEffect(() => {
    setError(null);
  }, [setError]);

  useEffect(() => {
    if (!phone || !code) {
      navigate(paths.FORGOT_PASSWORD);
    }
  }, [phone, code, navigate]);

  const formData: ResetPasswordFormData = {
    password,
    confirmPassword,
  };

  const onChangeField = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof ResetPasswordFormData
  ) => {
    const value = e.target.value;
    if (field === "password") {
      setPassword(value);
    } else {
      setConfirmPassword(value);
    }

    if (fieldsErrors[field]) {
      setFieldsErrors({ ...fieldsErrors, [field]: undefined });
    }
  };

  const onSubmit = async (data: ResetPasswordFormData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await newPasswordReq({
        phone,
        password: data.password,
      });

      if (response.data === true) {
        // Clear the form after successful password reset
        useResetPasswordStore.getState().reset();
        // Navigate to login page with success message
        navigate(paths.LOGIN, {
          state: { message: "Password reset successful. Please login." },
        });
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to reset password. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const setFieldErrorsHandler = (errors: FieldErrors) => {
    setFieldsErrors(errors);
  };

  const clearFieldErrorsHandler = () => {
    setFieldsErrors({});
  };

  return {
    formData,
    fieldsErrors,
    onSubmit,
    onChangeField,
    setFieldErrorsHandler,
    clearFieldErrorsHandler,
    isLoading,
    errorMessage,
  };
};
